import React, { useEffect, useState } from 'react';
import { Modal, Form, Input, DatePicker, InputNumber, Select, message, Upload } from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import type { UploadFile } from 'antd';
import apiService from '@/services/api.service';
import { API_ENDPOINTS } from '@/constants/api.constants';
import { logger } from '@/utils/logger';
import dayjs from 'dayjs';
import 'dayjs/locale/ru';
import { customLocale, getDateValueFromEvent, datePickerProps } from '@/utils/datePickerUtils';
import { CategoryService } from '@/services/category.service';
import { TournamentService } from '@/services/tournament.service';
import { TOURNAMENT_STATUS, TOURNAMENT_STATUS_LABELS } from '@/constants/tournament.constants';
import type { Category } from '@/types/models/tournament';
import { useAuth } from '@/contexts/AuthContext';
import UserService from '@/services/user.service';
import { ClubService } from '@/services/club.service';

// Настраиваем dayjs для русской локализации
dayjs.locale('ru');

interface EditTournamentModalProps {
    visible: boolean;
    tournament: any | null; // Используем any для избежания конфликтов типов между api.types.ts и models/tournament.ts
    onCancel: () => void;
    onSuccess: () => void;
}

export const EditTournamentModal: React.FC<EditTournamentModalProps> = ({
    visible,
    tournament,
    onCancel,
    onSuccess
}) => {
    const { user } = useAuth();
    const isAdmin = user?.role === 'admin';
    const [form] = Form.useForm();
    const [coverImageFileList, setCoverImageFileList] = useState<UploadFile[]>([]);
    const [documentFileList, setDocumentFileList] = useState<UploadFile[]>([]);
    const [categories, setCategories] = useState<Category[]>([]);
    const [loadingCategories, setLoadingCategories] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [users, setUsers] = useState<any[]>([]);
    const [clubs, setClubs] = useState<any[]>([]);
    const [loadingUsers, setLoadingUsers] = useState(false);
    const [loadingClubs, setLoadingClubs] = useState(false);

    // Загружаем категории, пользователей и клубы при открытии модального окна (только для админа)
    useEffect(() => {
        if (visible) {
            fetchCategories();
            if (isAdmin) {
                // Загружаем пользователей и клубы параллельно
                Promise.all([fetchUsers(), fetchClubs()]).catch(error => {
                    logger.error('Error loading users or clubs:', error);
                });
            }
        }
    }, [visible, isAdmin]);

    const fetchUsers = async () => {
        try {
            setLoadingUsers(true);
            const response = await UserService.getAllUsers({ limit: 1000 });
            // getAllUsers возвращает ApiResponse<{ rows: User[]; count: number }>
            const usersList = response.data?.rows || [];
            setUsers(usersList);
        } catch (error) {
            logger.error('Error fetching users:', error);
            message.error('Ошибка при загрузке списка пользователей');
        } finally {
            setLoadingUsers(false);
        }
    };

    const fetchClubs = async () => {
        try {
            setLoadingClubs(true);
            // Используем максимальный лимит, разрешенный бэкендом (1000)
            // Если клубов больше, можно сделать несколько запросов
            const response = await ClubService.getClubs({ limit: 1000 });
            // ClubService.getClubs возвращает PaginatedResponse<Club> с полями data.rows или data
            let clubsList: any[] = [];
            if (response.data) {
                if ('rows' in response.data && Array.isArray(response.data.rows)) {
                    clubsList = response.data.rows;
                } else if (Array.isArray(response.data)) {
                    clubsList = response.data;
                }
            }
            setClubs(clubsList);
            logger.info(`Loaded ${clubsList.length} clubs for tournament edit`);
        } catch (error) {
            logger.error('Error fetching clubs:', error);
            message.error('Ошибка при загрузке списка клубов');
        } finally {
            setLoadingClubs(false);
        }
    };

    const fetchCategories = async () => {
        try {
            setLoadingCategories(true);
            const fetchedCategories = await CategoryService.getAll();
            setCategories(fetchedCategories);
        } catch (error) {
            logger.error('Failed to fetch categories:', error);
            message.error('Не удалось загрузить категории');
        } finally {
            setLoadingCategories(false);
        }
    };

    useEffect(() => {
        if (!visible) {
            // Очищаем файлы при закрытии модального окна
            setCoverImageFileList([]);
            setDocumentFileList([]);
            return;
        }

        if (tournament) {
            // Извлекаем ID категорий из tournamentCategories
            const categoryIds: number[] = [];
            if (tournament.tournamentCategories && Array.isArray(tournament.tournamentCategories)) {
                tournament.tournamentCategories.forEach((tc: any) => {
                    if (tc && tc.weightCategory && tc.weightCategory.id) {
                        categoryIds.push(Number(tc.weightCategory.id));
                    }
                });
            }

            // Устанавливаем изображение обложки, если оно есть
            if (tournament.coverImage) {
                setCoverImageFileList([{
                    uid: '-1',
                    name: 'cover.jpg',
                    status: 'done',
                    url: tournament.coverImage,
                }]);
            }

            // Устанавливаем значения формы, включая organizerId и createdById
            // Значения будут правильно отображаться после загрузки пользователей и клубов
            form.setFieldsValue({
                name: tournament.name,
                description: tournament.description,
                location: tournament.location,
                tournamentDate: dayjs(tournament.tournamentDate),
                registrationDeadline: dayjs(tournament.registrationDeadline),
                weighingDates: tournament.weighingDates?.map((date: string) => dayjs(date)),
                feeForClubMembers: tournament.feeForClubMembers || 0,
                feeForOthers: tournament.feeForOthers || 0,
                status: tournament.status || 'draft',
                categoryIds: categoryIds.length > 0 ? categoryIds : undefined,
                organizerId: (tournament as any).organizerId || undefined,
                createdById: (tournament as any).createdById || undefined,
            });
        }
    }, [visible, tournament, form]);

    // Обновляем значения organizerId и createdById после загрузки пользователей и клубов
    useEffect(() => {
        if (visible && tournament && (users.length > 0 || clubs.length > 0)) {
            const currentOrganizerId = form.getFieldValue('organizerId');
            const currentCreatedById = form.getFieldValue('createdById');
            
            // Проверяем, что значения еще не установлены или нужно обновить
            if (tournament.organizerId && currentOrganizerId !== tournament.organizerId) {
                form.setFieldValue('organizerId', tournament.organizerId);
            }
            if (tournament.createdById && currentCreatedById !== tournament.createdById) {
                form.setFieldValue('createdById', tournament.createdById);
            }
        }
    }, [visible, tournament, users, clubs, form]);

    const handleSubmit = async () => {
        if (!tournament) return;

        try {
            setSubmitting(true);
            const values = await form.validateFields();

            // Создаем FormData для отправки данных и файлов
            const formData = new FormData();

            // Добавляем все поля турнира в FormData
            formData.append('name', values.name);
            formData.append('description', values.description || '');
            formData.append('location', values.location);
            formData.append('tournamentDate', dayjs(values.tournamentDate).format('YYYY-MM-DD'));
            formData.append('registrationDeadline', dayjs(values.registrationDeadline).format('YYYY-MM-DD'));
            
            if (values.weighingDates && Array.isArray(values.weighingDates)) {
                values.weighingDates.forEach((date: dayjs.Dayjs, index: number) => {
                    formData.append(`weighingDates[${index}]`, date.format('YYYY-MM-DD'));
                });
            }
            
            formData.append('feeForClubMembers', String(values.feeForClubMembers || 0));
            formData.append('feeForOthers', String(values.feeForOthers || 0));
            formData.append('status', values.status || 'draft');
            
            // Добавляем organizerId только если это админ
            // Отправляем даже если значение undefined/null, чтобы бэкенд мог обработать изменение
            if (isAdmin) {
                if (values.organizerId !== undefined && values.organizerId !== null) {
                    formData.append('organizerId', String(values.organizerId));
                } else if (values.organizerId === null || values.organizerId === '') {
                    // Если поле очищено, отправляем пустую строку для явного указания null
                    formData.append('organizerId', '');
                }
                
                // Добавляем createdById только если это админ
                if (values.createdById !== undefined && values.createdById !== null) {
                    formData.append('createdById', String(values.createdById));
                } else if (values.createdById === null || values.createdById === '') {
                    // Если поле очищено, отправляем пустую строку для явного указания null
                    formData.append('createdById', '');
                }
            }
            
            if (values.categoryIds && Array.isArray(values.categoryIds)) {
                values.categoryIds.forEach((categoryId: number, index: number) => {
                    formData.append(`categoryIds[${index}]`, String(categoryId));
                });
            }

            // ВАЖНО: Отправляем обложку как первый файл в массиве files
            // Backend определяет первое изображение как coverImage
            if (coverImageFileList.length > 0 && coverImageFileList[0].originFileObj) {
                formData.append('files', coverImageFileList[0].originFileObj);
            }

            // Добавляем документы после обложки (если она есть)
            documentFileList.forEach((file) => {
                if (file.originFileObj) {
                    formData.append('files', file.originFileObj);
                }
            });

            // Отправляем FormData через PUT запрос
            await apiService.put(`${API_ENDPOINTS.TOURNAMENTS.BASE}/${tournament.id}`, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });

            // Обновляем связи с категориями, если они выбраны
            if (values.categoryIds && Array.isArray(values.categoryIds) && values.categoryIds.length > 0) {
                try {
                    const categoryData = values.categoryIds.map((categoryId: number) => ({
                        tournamentId: tournament.id,
                        categoryId: categoryId,
                        status: 'active' as const
                    }));

                    await TournamentService.updateCategories(tournament.id, categoryData);
                    logger.info(`Categories updated for tournament ${tournament.id}`);
                } catch (categoryError) {
                    logger.error('Error updating categories to tournament:', categoryError);
                    // Не прерываем обновление турнира, если категории не обновились
                }
            }

            message.success('Турнир успешно обновлен');
            form.resetFields();
            setCoverImageFileList([]);
            setDocumentFileList([]);
            onSuccess();
            // Родитель обновит контент через onSuccess, остаёмся на странице
        } catch (error) {
            logger.error('Error updating tournament:', error);
            message.error('Ошибка при обновлении турнира');
        } finally {
            setSubmitting(false);
        }
    };

    const beforeUploadCoverImage = (file: File) => {
        const isImage = file.type.startsWith('image/');
        if (!isImage) {
            message.error('Можно загружать только изображения!');
            return false;
        }
        const isLt5M = file.size / 1024 / 1024 < 5;
        if (!isLt5M) {
            message.error('Изображение должно быть меньше 5MB!');
            return false;
        }
        return false; // Предотвращаем автоматическую загрузку
    };

    const beforeUploadDocument = (file: File) => {
        // Проверяем, что это не изображение (документы только)
        const isImage = file.type.startsWith('image/');
        if (isImage) {
            message.error('Для загрузки изображений используйте поле "Фото турнира". Здесь можно загружать только документы!');
            return false;
        }
        const isLt10M = file.size / 1024 / 1024 < 10;
        if (!isLt10M) {
            message.error('Файл должен быть меньше 10MB!');
            return false;
        }
        return false; // Предотвращаем автоматическую загрузку
    };

    const handleCoverImageChange = (info: any) => {
        setCoverImageFileList(info.fileList.slice(-1)); // Разрешаем только одно изображение
    };

    const handleDocumentChange = (info: any) => {
        setDocumentFileList(info.fileList);
    };

    const handleCancel = () => {
        form.resetFields();
        setCoverImageFileList([]);
        setDocumentFileList([]);
        onCancel();
    };

    return (
        <Modal
            title="Редактировать турнир"
            open={visible}
            onCancel={handleCancel}
            onOk={handleSubmit}
            okText="Сохранить"
            cancelText="Отмена"
            width={800}
            confirmLoading={submitting}
            destroyOnClose={true}
        >
            <Form
                form={form}
                layout="vertical"
            >
                {/* Поле для загрузки фото - в самом верху */}
                <Form.Item
                    name="coverImage"
                    label="Фото турнира"
                    extra="Рекомендуемый размер: не более 5MB, форматы: JPEG, PNG"
                >
                    <Upload
                        listType="picture-card"
                        fileList={coverImageFileList}
                        onChange={handleCoverImageChange}
                        beforeUpload={beforeUploadCoverImage}
                        accept="image/*"
                        maxCount={1}
                        onRemove={() => setCoverImageFileList([])}
                    >
                        {coverImageFileList.length < 1 && (
                            <div>
                                <UploadOutlined />
                                <div style={{ marginTop: 8 }}>Загрузить</div>
                            </div>
                        )}
                    </Upload>
                </Form.Item>

                <Form.Item
                    name="name"
                    label="Название"
                    rules={[{ required: true, message: 'Введите название турнира' }]}
                >
                    <Input />
                </Form.Item>

                <Form.Item
                    name="description"
                    label="Описание"
                >
                    <Input.TextArea rows={4} />
                </Form.Item>

                <Form.Item
                    name="location"
                    label="Место проведения"
                    rules={[{ required: true, message: 'Укажите место проведения' }]}
                >
                    <Input />
                </Form.Item>

                <Form.Item
                    name="tournamentDate"
                    label="Дата проведения"
                    rules={[{ required: true, message: 'Укажите дату проведения' }]}
                    getValueFromEvent={getDateValueFromEvent}
                >
                    <DatePicker 
                        {...datePickerProps}
                        placeholder="Выберите дату (ДД.ММ.ГГГГ)"
                    />
                </Form.Item>

                <Form.Item
                    name="registrationDeadline"
                    label="Окончание регистрации"
                    rules={[{ required: true, message: 'Укажите окончание регистрации' }]}
                    getValueFromEvent={getDateValueFromEvent}
                >
                    <DatePicker 
                        {...datePickerProps}
                        placeholder="Выберите дату (ДД.ММ.ГГГГ)"
                    />
                </Form.Item>

                <Form.Item
                    name="weighingDates"
                    label="Даты взвешивания"
                >
                    <DatePicker.RangePicker 
                        style={{ width: '100%' }}
                        format="DD.MM.YYYY"
                        locale={customLocale}
                        placeholder={['Начало', 'Конец']}
                        disabledDate={() => false}
                    />
                </Form.Item>

                <Form.Item
                    name="feeForClubMembers"
                    label="Взнос для членов клуба"
                    initialValue={0}
                >
                    <InputNumber<number>
                        style={{ width: '100%' }}
                        min={0}
                        precision={2}
                        formatter={(value) => `${value} ₽`}
                        parser={(value) => {
                            const parsed = value?.replace(' ₽', '').replace(/\s/g, '') || '';
                            return parsed ? parseFloat(parsed) : 0;
                        }}
                    />
                </Form.Item>

                <Form.Item
                    name="feeForOthers"
                    label="Взнос для остальных участников"
                    initialValue={0}
                >
                    <InputNumber<number>
                        style={{ width: '100%' }}
                        min={0}
                        precision={2}
                        formatter={(value) => `${value} ₽`}
                        parser={(value) => {
                            const parsed = value?.replace(' ₽', '').replace(/\s/g, '') || '';
                            return parsed ? parseFloat(parsed) : 0;
                        }}
                    />
                </Form.Item>

                <Form.Item
                    name="categoryIds"
                    label="Категории"
                >
                    <Select
                        mode="multiple"
                        placeholder="Выберите категории"
                        style={{ width: '100%' }}
                        loading={loadingCategories}
                        optionFilterProp="children"
                        showSearch
                    >
                        {categories.map((category) => (
                            <Select.Option
                                key={category.id}
                                value={category.id}
                            >
                                {category.name} ({category.ageFrom}-{category.ageTo} лет, {category.weightFrom}-{category.weightTo} кг, {category.gender})
                            </Select.Option>
                        ))}
                    </Select>
                </Form.Item>

                <Form.Item
                    name="status"
                    label="Статус"
                    initialValue="draft"
                >
                    <Select placeholder="Выберите статус турнира">
                        {Object.entries(TOURNAMENT_STATUS)
                            .filter(([key]) => key !== 'DELETED')
                            .map(([key, value]) => (
                                <Select.Option key={key} value={value}>
                                    {TOURNAMENT_STATUS_LABELS[value]}
                                </Select.Option>
                            ))}
                    </Select>
                </Form.Item>

                {isAdmin && (
                    <>
                        <Form.Item
                            name="organizerId"
                            label="Владелец турнира"
                            extra="Выберите пользователя или клуб в качестве владельца турнира"
                        >
                            <Select
                                placeholder="Выберите владельца"
                                showSearch
                                allowClear
                                loading={loadingUsers || loadingClubs}
                                optionFilterProp="children"
                                filterOption={(input, option) => {
                                    const children: any = option?.children;
                                    const inputLower = input.toLowerCase();
                                    
                                    if (typeof children === 'string') {
                                        return children.toLowerCase().includes(inputLower);
                                    }
                                    
                                    if (Array.isArray(children)) {
                                        return children.some((child: any) => {
                                            if (typeof child === 'string') {
                                                return child.toLowerCase().includes(inputLower);
                                            }
                                            return false;
                                        });
                                    }
                                    
                                    if (children) {
                                        // Handle React elements or other objects
                                        const text = String(children);
                                        return text.toLowerCase().includes(inputLower);
                                    }
                                    
                                    return false;
                                }}
                                notFoundContent={loadingUsers || loadingClubs ? 'Загрузка...' : 'Нет данных'}
                            >
                                <Select.OptGroup label="Пользователи">
                                    {users.map((user) => (
                                        <Select.Option key={`user-${user.id}`} value={user.id} label={`${user.firstName} ${user.lastName} (${user.email}) - ${user.role}`}>
                                            {user.firstName} {user.lastName} ({user.email}) - {user.role}
                                        </Select.Option>
                                    ))}
                                </Select.OptGroup>
                                <Select.OptGroup label="Клубы">
                                    {clubs.map((club) => (
                                        <Select.Option key={`club-${club.id}`} value={club.id} label={`${club.name} (${club.city})`}>
                                            {club.name} ({club.city})
                                        </Select.Option>
                                    ))}
                                </Select.OptGroup>
                            </Select>
                        </Form.Item>

                        <Form.Item
                            name="createdById"
                            label="Создатель турнира"
                            extra="Выберите пользователя, который создал турнир"
                        >
                            <Select
                                placeholder="Выберите создателя"
                                showSearch
                                allowClear
                                loading={loadingUsers}
                                optionFilterProp="children"
                                filterOption={(input, option) => {
                                    const children: any = option?.children;
                                    const inputLower = input.toLowerCase();
                                    
                                    if (typeof children === 'string') {
                                        return children.toLowerCase().includes(inputLower);
                                    }
                                    
                                    if (Array.isArray(children)) {
                                        return children.some((child: any) => {
                                            if (typeof child === 'string') {
                                                return child.toLowerCase().includes(inputLower);
                                            }
                                            return false;
                                        });
                                    }
                                    
                                    if (children) {
                                        const text = String(children);
                                        return text.toLowerCase().includes(inputLower);
                                    }
                                    
                                    return false;
                                }}
                                notFoundContent={loadingUsers ? 'Загрузка...' : 'Нет данных'}
                            >
                                {users.map((user) => (
                                    <Select.Option key={`creator-${user.id}`} value={user.id} label={`${user.firstName} ${user.lastName} (${user.email}) - ${user.role}`}>
                                        {user.firstName} {user.lastName} ({user.email}) - {user.role}
                                    </Select.Option>
                                ))}
                            </Select>
                        </Form.Item>
                    </>
                )}

                <Form.Item
                    name="documents"
                    label="Документы"
                    extra="Можно загружать документы (PDF, DOC, DOCX и др.). Максимальный размер файла: 10MB"
                >
                    <Upload
                        fileList={documentFileList}
                        onChange={handleDocumentChange}
                        beforeUpload={beforeUploadDocument}
                        multiple
                    >
                        <div>
                            <UploadOutlined /> Загрузить документы
                        </div>
                    </Upload>
                </Form.Item>
            </Form>
        </Modal>
    );
};